import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Layouts, Layout } from 'react-grid-layout';
import { DesktopUIWindow, LayoutBreakpoint } from './types';
import {
  LOCAL_STORAGE_LAYOUT_KEY,
  LOCAL_STORAGE_DESKTOP_KEY,
  defaultWindowsPositions,
  INITIAL_STATE_CONFIG,
} from './config';
import { safeParseJson } from '~/shared/utils/storage';

/**
 * Desktop Slice
 *
 * Manages the state for the desktop grid, including windows, layouts, and focus.
 *
 * IMPORTANT: Persistence to localStorage
 * ========================================
 * Any action that modifies `desktopWindows` or `layouts` MUST be registered
 * in `src/core/middleware/desktopStorageMiddleware.ts` to ensure the state is
 * persisted to localStorage.
 *
 * When adding a new action:
 * 1. Add the action to this slice
 * 2. Import the action in desktopStorageMiddleware.ts
 * 3. Add `yourAction.match(action) ||` to the middleware's if condition
 *
 * Example:
 *   import { yourAction } from '~/features/Desktop/DesktopSlice';
 *
 *   if (
 *     removeWindow.match(action) ||
 *     yourAction.match(action) ||  // ← Add your action here
 *     // ... other actions
 *   ) {
 *     // Persist to localStorage
 *   }
 */

interface State {
  desktopWindows: DesktopUIWindow[];
  layouts: Layouts;
  focusedWindowId: string | null;
}

function isDesktopUIWindowArray(value: unknown): value is DesktopUIWindow[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        item != null &&
        typeof item === 'object' &&
        typeof item.id === 'string' &&
        typeof item.name === 'string'
    )
  );
}

function isLayouts(value: unknown): value is Layouts {
  if (value == null || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.lg) && Array.isArray(obj.md) && Array.isArray(obj.sm)
  );
}

const defaultLayouts: Layouts = { xl: [], lg: [], md: [], sm: [] };

const storedWindows = safeParseJson<DesktopUIWindow[]>(
  LOCAL_STORAGE_DESKTOP_KEY,
  [],
  isDesktopUIWindowArray
);

const hasStoredWindows = storedWindows.length > 0;
const desktopWindows: DesktopUIWindow[] = hasStoredWindows
  ? storedWindows
  : INITIAL_STATE_CONFIG.initialWindows;

const rawLayouts = safeParseJson<Layouts>(
  LOCAL_STORAGE_LAYOUT_KEY,
  hasStoredWindows ? defaultLayouts : INITIAL_STATE_CONFIG.initialLayouts,
  isLayouts
);
// Migrate old layouts to include xl breakpoint
const layouts: Layouts = {
  xl: (rawLayouts as any).xl || [],
  lg: rawLayouts.lg || [],
  md: rawLayouts.md || [],
  sm: rawLayouts.sm || [],
};

const isFirstVisit = !hasStoredWindows;
const initialState: State = {
  desktopWindows,
  layouts,
  focusedWindowId:
    isFirstVisit && desktopWindows.length > 0 ? desktopWindows[0].id : null,
};

export const DesktopSlice = createSlice({
  name: 'Desktop',
  initialState,
  reducers: {
    removeWindow: (state, action: PayloadAction<string>) => {
      const index = state.desktopWindows.findIndex(
        (window) => window.id === action.payload
      );
      if (index !== -1) {
        if (state.focusedWindowId === action.payload) {
          state.focusedWindowId = null;
        }
        state.desktopWindows.splice(index, 1);

        const breakpoints: LayoutBreakpoint[] = ['xl', 'lg', 'md', 'sm'];
        breakpoints.forEach((breakpoint) => {
          state.layouts[breakpoint] = state.layouts[breakpoint].filter(
            (layout) => layout.i !== action.payload
          );
        });
      }
    },
    addWindow: (state, action: PayloadAction<DesktopUIWindow>) => {
      if (
        !state.desktopWindows.find((window) => window.id === action.payload.id)
      ) {
        state.desktopWindows.unshift(action.payload);
        state.focusedWindowId = action.payload.id;

        // Calculate position based on existing windows
        const windowCount = state.layouts.lg.length;

        // Extra Large (>1920px): 3 windows per row (4 cols each)
        const xlCol = (windowCount % 3) * 4;
        const xlRow = Math.floor(windowCount / 3) * 4;

        // Large: 3 windows per row (4 cols each)
        const lgCol = (windowCount % 3) * 4;
        const lgRow = Math.floor(windowCount / 3) * 4;

        // Medium: 2 windows per row (4 cols each)
        const mdCol = (windowCount % 2) * 4;
        const mdRow = Math.floor(windowCount / 2) * 4;

        const xl: Layout = action.payload.layout?.xl ?? {
          ...defaultWindowsPositions.xl,
          i: `${action.payload.id}`,
          x: xlCol,
          y: xlRow,
        };
        const lg: Layout = action.payload.layout?.lg ?? {
          ...defaultWindowsPositions.lg,
          i: `${action.payload.id}`,
          x: lgCol,
          y: lgRow,
        };
        const md: Layout = action.payload.layout?.md ?? {
          ...defaultWindowsPositions.md,
          i: `${action.payload.id}`,
          x: mdCol,
          y: mdRow,
        };
        const sm: Layout = action.payload.layout?.sm ?? {
          ...defaultWindowsPositions.sm,
          i: `${action.payload.id}`,
          x: 0,
          y: windowCount * 4,
        };

        state.layouts.xl.push(xl);
        state.layouts.lg.push(lg);
        state.layouts.md.push(md);
        state.layouts.sm.push(sm);
      }
    },
    updateLayouts: (
      state,
      action: PayloadAction<{ layout: Layout[]; breakpoint: LayoutBreakpoint }>
    ) => {
      state.layouts = {
        ...state.layouts,
        [action.payload.breakpoint]: action.payload.layout,
      };
    },
    setFocus: (state, action: PayloadAction<string | null>) => {
      state.focusedWindowId = action.payload;
    },
    resetLayouts: (state) => {
      // Recreate layouts for all existing windows
      const newLayouts: Layouts = { xl: [], lg: [], md: [], sm: [] };

      state.desktopWindows.forEach((window, index) => {
        // Extra Large screens (>1920px): 3 per row, 4 cols each
        newLayouts.xl.push({
          i: window.id,
          x: (index % 3) * 4,
          y: Math.floor(index / 3) * 4,
          w: 4,
          h: 4,
        });

        // Large screens: 3 per row, 4 cols each (consistent with addWindow)
        newLayouts.lg.push({
          i: window.id,
          x: (index % 3) * 4,
          y: Math.floor(index / 3) * 4,
          w: 4,
          h: 4,
        });

        // Medium screens: 2 per row, 4 cols wide, 4 rows tall
        newLayouts.md.push({
          i: window.id,
          x: (index % 2) * 4,
          y: Math.floor(index / 2) * 4,
          w: 4,
          h: 4,
        });

        // Small screens: full width, stacked vertically
        newLayouts.sm.push({
          i: window.id,
          x: 0,
          y: index * 3,
          w: 2,
          h: 3,
        });
      });

      state.layouts = newLayouts;
    },
    removeAllWindows: (state) => {
      state.desktopWindows = [];
      state.layouts = defaultLayouts;
      state.focusedWindowId = null;
    },
    organizeGrid: (state) => {
      // Organize all windows in an equal-sized grid
      const newLayouts: Layouts = { xl: [], lg: [], md: [], sm: [] };

      state.desktopWindows.forEach((window, index) => {
        // Extra Large screens (>1920px): 3 columns per row (4 cols each), 3 rows tall
        const xlCols = 3;
        const xlWidth = 4;
        const xlHeight = 3;
        const xlCol = (index % xlCols) * xlWidth;
        const xlRow = Math.floor(index / xlCols) * xlHeight;

        newLayouts.xl.push({
          i: window.id,
          x: xlCol,
          y: xlRow,
          w: xlWidth,
          h: xlHeight,
        });

        // Large screens: 4 columns per row (3 cols each), 3 rows tall
        const lgCols = 4;
        const lgWidth = 3;
        const lgHeight = 3;
        const lgCol = (index % lgCols) * lgWidth;
        const lgRow = Math.floor(index / lgCols) * lgHeight;

        newLayouts.lg.push({
          i: window.id,
          x: lgCol,
          y: lgRow,
          w: lgWidth,
          h: lgHeight,
        });

        // Medium screens: 2 columns per row (4 cols each), 3 rows tall
        const mdCols = 2;
        const mdWidth = 4;
        const mdHeight = 3;
        const mdCol = (index % mdCols) * mdWidth;
        const mdRow = Math.floor(index / mdCols) * mdHeight;

        newLayouts.md.push({
          i: window.id,
          x: mdCol,
          y: mdRow,
          w: mdWidth,
          h: mdHeight,
        });

        // Small screens: 1 column, stacked vertically
        newLayouts.sm.push({
          i: window.id,
          x: 0,
          y: index * 3,
          w: 2,
          h: 3,
        });
      });

      state.layouts = newLayouts;
    },
  },
});

export const {
  removeWindow,
  addWindow,
  updateLayouts,
  setFocus,
  resetLayouts,
  removeAllWindows,
  organizeGrid,
} = DesktopSlice.actions;

/**
 * REMINDER: When adding new actions that modify `desktopWindows` or `layouts`,
 * you MUST register them in `src/middleware/desktopStorageMiddleware.ts`
 * to ensure persistence to localStorage.
 *
 * See docs/MIDDLEWARE_PERSISTENCE.md for details.
 */

export default DesktopSlice.reducer;
